import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DOMUtils from '@spothero/utils/dom';
import Link from 'router/Link';
import ExperimentUtils, {IMMEDIATE_FEATURES} from 'utils/experiment';
import UserUtils from 'utils/user-utils';

export default class NavigationExpanded extends PureComponent {
    static propTypes = {
        forceAnchors: PropTypes.bool,
        isProduction: PropTypes.bool,
        user: PropTypes.object,
        items: PropTypes.arrayOf(PropTypes.object).isRequired,
        onItemSelected: PropTypes.func,
    };

    componentDidMount() {
        DOMUtils.addEventListener({
            event: 'click',
            target: '.NavigationExpanded-dropdown-item',
            handler: this._onDropdownItemClick,
        });
    }

    componentWillUnmount() {
        DOMUtils.removeEventListener({
            event: 'click',
            target: '.NavigationExpanded-dropdown-item',
            handler: this._onDropdownItemClick,
        });
    }

    _onNavItemEnter = evt => {
        DOMUtils.addClass(
            evt.currentTarget,
            'NavigationExpanded-item-container-hover'
        );
    };

    _onNavItemLeave = evt => {
        DOMUtils.removeClass(
            evt.currentTarget,
            'NavigationExpanded-item-container-hover'
        );
    };

    _onMultiLevelMenuItemEnter = evt => {
        const categories = [
            ...DOMUtils.el('.NavigationExpanded-multi-level-menu-item'),
        ];
        const itemHoverClass = 'NavigationExpanded-multi-level-menu-item-hover';
        const listHoverClass =
            'NavigationExpanded-multi-level-menu-item-list-hover';

        categories.forEach(item => {
            const id = DOMUtils.attr(item, 'data-menu-id');
            const list = `.NavigationExpanded-multi-level-menu-item-list-${id}`;

            if (item === evt.currentTarget) {
                DOMUtils.addClass(item, itemHoverClass);
                DOMUtils.addClass(list, listHoverClass);
            } else {
                DOMUtils.removeClass(item, itemHoverClass);
                DOMUtils.removeClass(list, listHoverClass);
            }
        });
    };

    _onNavItemClick = evt => {
        const {onItemSelected} = this.props;

        this._onNavItemLeave(evt);

        if (onItemSelected) {
            onItemSelected(evt);
        }
    };

    _onDropdownItemClick = evt => {
        const {onItemSelected} = this.props;
        const container = DOMUtils.parent(
            evt.target,
            '.NavigationExpanded-item-container'
        );

        DOMUtils.removeClass(
            container,
            'NavigationExpanded-item-container-hover'
        );

        if (onItemSelected) {
            onItemSelected(evt);
        }
    };

    _renderLinkItem({item, key}) {
        const {forceAnchors} = this.props;

        return (
            <Link
                key={key}
                className="NavigationExpanded-dropdown-item"
                to={item.url}
                forceAnchor={forceAnchors}
            >
                {item.title}
            </Link>
        );
    }

    _renderMultiLevelMenu(items) {
        const {forceAnchors} = this.props;

        return (
            <div className="NavigationExpanded-multi-level-menu">
                <div className="NavigationExpanded-multi-level-menu-categories">
                    {items.map((item, i) => {
                        const classes = classNames(
                            'NavigationExpanded-multi-level-menu-item',
                            {
                                'NavigationExpanded-multi-level-menu-item-hover':
                                    i === 0,
                            }
                        );

                        return (
                            <div
                                key={i}
                                className={classes}
                                data-menu-id={i}
                                onMouseEnter={this._onMultiLevelMenuItemEnter}
                            >
                                <Link
                                    to={item.url || '#'}
                                    forceAnchor={forceAnchors}
                                >
                                    {item.title}
                                </Link>
                            </div>
                        );
                    })}
                </div>
                <div className="NavigationExpanded-multi-level-menu-items">
                    {items.map((item, i) => {
                        const classes = classNames(
                            'NavigationExpanded-multi-level-menu-item-list',
                            `NavigationExpanded-multi-level-menu-item-list-${i}`,
                            {
                                'NavigationExpanded-multi-level-menu-item-list-hover':
                                    i === 0,
                            }
                        );

                        return (
                            <div key={i} className={classes}>
                                {item.items.map((link, j) => {
                                    return this._renderLinkItem({
                                        key: j,
                                        item: link,
                                    });
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    render() {
        const {
            forceAnchors,
            isProduction,
            user: {isAdmin, status},
            items,
        } = this.props;

        return (
            <nav className="NavigationExpanded" role="navigation">
                <ul>
                    {items.map((item, i) => {
                        const classes = classNames(
                            'NavigationExpanded-item',
                            item.classes
                        );

                        return (
                            <li key={i}>
                                <div
                                    className="NavigationExpanded-item-container"
                                    onMouseEnter={this._onNavItemEnter}
                                    onMouseLeave={this._onNavItemLeave}
                                    onClick={this._onNavItemClick}
                                >
                                    <Link
                                        className={classes}
                                        to={item.url || '#'}
                                        forceAnchor={forceAnchors}
                                    >
                                        {item.title}
                                    </Link>
                                    {item.items && (
                                        <div className="NavigationExpanded-menu">
                                            {item.items.some(menu =>
                                                Boolean(menu.items)
                                            ) ? (
                                                this._renderMultiLevelMenu(
                                                    item.items
                                                )
                                            ) : (
                                                <div className="NavigationExpanded-dropdown">
                                                    {item.items.map(
                                                        (ddItem, j) => {
                                                            return this._renderLinkItem(
                                                                {
                                                                    key: j,
                                                                    item: ddItem,
                                                                }
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                    {!isProduction && (
                        <li>
                            {status === UserUtils.AUTH_STATE.USER ? (
                                <Link
                                    className="NavigationExpanded-item"
                                    to="/logout"
                                    forceAnchor={forceAnchors}
                                >
                                    Sign Out
                                </Link>
                            ) : (
                                <Link
                                    className="NavigationExpanded-item"
                                    to={
                                        ExperimentUtils.isFeatureEnabled(
                                            IMMEDIATE_FEATURES.AUTH0_ROLLOUT_FEATURE
                                        )
                                            ? '/auth/restricted-login'
                                            : '/login'
                                    }
                                    forceAnchor
                                >
                                    Sign In
                                </Link>
                            )}
                        </li>
                    )}
                    {isAdmin && (
                        <li>
                            <Link
                                className="NavigationExpanded-item admin-link"
                                to="/admin"
                                forceAnchor
                            >
                                Admin
                            </Link>
                        </li>
                    )}
                </ul>
            </nav>
        );
    }
}
